/**
 * XML 结构自检（生成后，针对已序列化的 ReportIR）。
 * 校验：坐标唯一性、合并越界、父格成环、数据集字段引用有效性。
 * 返回可定位到具体单元格的错误列表（不抛异常，供 CLI 汇总输出）。
 */

import type { ReportIR, ParameterIR, CellIR } from '../ir/types.js';
import { expandGrid, GridError } from '../grid/expand.js';
import { detectParentCycle, normalizeTopParentCells } from '../grid/parent.js';
import { coordName } from '../grid/coordinate.js';
import { extractParamPlaceholders, isParamName } from '../ir/types.js';

/** editorType 别名 → 官方标准值（与 serialize/parse 的归一逻辑保持一致）。 */
const EDITOR_TYPE_ALIAS: Record<string, string> = {
  textarea: 'text',
  input: 'text',
  dropdown: 'select',
  combo: 'select',
  combobox: 'select',
  datepicker: 'date',
  calendar: 'date',
};

export interface StructureIssue {
  level: 'error' | 'warn';
  cell?: string; // A1 风格坐标
  message: string;
}

/**
 * 简单 Levenshtein 编辑距离，用于参数名拼写错误的「你是不是想写 X」建议。
 */
function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i]![0] = i;
  for (let j = 0; j <= n; j++) dp[0]![j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i]![j] = Math.min(dp[i - 1]![j]! + 1, dp[i]![j - 1]! + 1, dp[i - 1]![j - 1]! + cost);
    }
  }
  return dp[m]![n]!;
}

/** 从候选参数名中找与 target 最相近（编辑距离最小且 ≤2）的一个，供修正建议。 */
function suggestParamName(target: string, candidates: string[]): string | undefined {
  let best: string | undefined;
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = editDistance(target.toLowerCase(), c.toLowerCase());
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }
  return bestDist <= 2 ? best : undefined;
}

export function structureCheck(report: ReportIR, opts?: { requireGrid?: boolean }): StructureIssue[] {
  const issues: StructureIssue[] = [];

  // 0. 强制行列定义（requireGrid）：
  //    触发条件：显式 --require-grid，或 IR 自声明 source:'template'（由 Excel 模板转换生成）。
  //    模板骨架的 rows[].height/columns[].width 已是引擎单位，须从骨架原样搬入 IR；
  //    缺失时序列化会静默补默认行高 26/列宽 80 导致版式失真，故在模板流程直接报错拦截。
  //    普通手写 IR（source 缺省或 'handwritten'）允许省略 rows/columns（未列出的行取默认行高）。
  //    让 source:'template' 自动强制，即从源头杜绝「AI 忘了传 --require-grid」导致的版式失真。
  const mustRequireGrid = !!opts?.requireGrid || report.source === 'template';
  if (mustRequireGrid) {
    if (!report.rows || report.rows.length === 0) {
      issues.push({
        level: 'error',
        message:
          '缺少 rows 行高定义（模板报表必须含 rows[].height）：模板转换必须从骨架原样搬入 rows[].height（引擎单位），否则全部行退化为默认高度 26。请确认 IR 顶层声明 "source": "template" 且已从模板骨架搬入 rows/columns。',
      });
    }
    if (!report.columns || report.columns.length === 0) {
      issues.push({
        level: 'error',
        message:
          '缺少 columns 列宽定义（模板报表必须含 columns[].width）：模板转换必须从骨架原样搬入 columns[].width（引擎单位），否则全部列退化为默认宽度 80。请确认 IR 顶层声明 "source": "template" 且已从模板骨架搬入 rows/columns。',
      });
    }
    if (issues.length) return issues;
  }

  // 1. 网格展开（合并越界/重叠/重复由 expandGrid 抛错，这里捕获为 issue）
  let grid;
  try {
    grid = expandGrid(report);
  } catch (e) {
    if (e instanceof GridError) {
      issues.push({ level: 'error', message: e.message });
      return issues;
    }
    throw e;
  }

  // 2. 父格成环（先规范化顶层父格码，再检测左/上父格交叉环）
  const cycle = detectParentCycle(normalizeTopParentCells(report.cells));
  if (cycle) issues.push({ level: 'error', message: cycle });

  // 3. 数据集字段引用有效性
  const datasets = new Map((report.datasets ?? []).map((d) => [d.name, d]));
  for (const c of report.cells) {
    const ref = c.content;
    if (ref?.kind === 'dataset') {
      const ds = datasets.get(ref.dataset);
      const where = `${coordName(c.row, c.col)} (${ref.dataset}.${ref.field})`;
      if (!ds) {
        issues.push({ level: 'error', cell: coordName(c.row, c.col), message: `${where}: 引用的数据集不存在` });
        continue;
      }
      if (!ds.fields.some((f) => f.name === ref.field)) {
        issues.push({
          level: 'error',
          cell: coordName(c.row, c.col),
          message: `${where}: 字段在数据集 ${ref.dataset} 中未定义`,
        });
      }
    }
  }

  // 3b. 单元格样式/聚合枚举合法性（对应官方 FormatType.valueOf / AggregateType.valueOf
  //     无空保护，非法值会在引擎端 NPE。提前在这里拦截，避免生成无法解析的 XML）。
  const FORMAT_TYPES = new Set(['text', 'date', 'currency', 'number', 'custom']);
  const AGG_TYPES = new Set([
    // IR 层 'none' 表示「不聚合（明细）」，serialize 会翻译为官方 'select'，故此处合法。
    'none', 'group', 'conditiongroup', 'expressiongroup', 'select', 'sum', 'avg',
    'max', 'min', 'count', 'countd', 'percentile', 'variance', 'std',
  ]);
  for (const c of report.cells) {
    const where = coordName(c.row, c.col);
    const fmt = c.style?.formatType;
    if (fmt && !FORMAT_TYPES.has(fmt)) {
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.style.formatType=${fmt}: 非法格式化类型（官方 FormatType 仅支持 ${[...FORMAT_TYPES].join('/')}）`,
      });
    }
    // 缺省按 select（明细）归并：与 serialize 的兜底逻辑保持一致，
    // 使「IR 缺失 aggregate」与「显式 select」在校验/序列化两端行为完全一致。
    const agg = c.content?.kind === 'dataset' ? (c.content.aggregate ?? 'select') : undefined;
    if (agg && !AGG_TYPES.has(agg)) {
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.content.aggregate=${agg}: 非法聚合类型（官方 AggregateType 不含 'none' 或任意未识别值，缺省请用 'select'）`,
      });
    }
  }

  // 3b-1. 绑定数据字段且需「逐条展开」的单元格，扩展方向 expand 不能为 none。
  //       select/group/conditiongroup/expressiongroup 表示按数据逐条展开（明细/分组），
  //       缺省按 down 归并（纵向明细的默认方向）；显式 none 会报错，
  //       否则引擎无法确定扩展轴向而渲染错位。
  //       sum/count/avg/max/min 等「聚合取值格」取单值不扩展，允许 expand: none（静态）。
  //       注意：明细标准写法是 aggregate: "select"（IR 层旧的 none 别名已废弃，见 cell-and-expand.md）。
  const EXPANDABLE_AGG = new Set(['select', 'group', 'conditiongroup', 'expressiongroup']);
  for (const c of report.cells) {
    if (c.content?.kind !== 'dataset') continue;
    // 缺省按 select（明细）归并：与 serialize 的兜底逻辑保持一致，
    // 使「IR 缺失 aggregate」与「显式 select」在校验/序列化两端行为完全一致。
    const agg = c.content.aggregate ?? 'select';
    if (!EXPANDABLE_AGG.has(agg)) continue; // 聚合取值格（sum 等）/ 已废弃的 none 不在此约束
    // 缺省按 down（纵向明细默认方向）归并：与 serialize 对可展开明细格的兜底一致，
    // 使「IR 缺失 expand」与「显式 expand:"down"」在校验/序列化两端行为完全一致。
    const expand = c.expand ?? 'down';
    if (expand !== 'down' && expand !== 'right') {
      const where = coordName(c.row, c.col);
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}: 绑定数据字段且 aggregate=${agg} 的单元格扩展方向必须为 "down" 或 "right"（缺省按 down），不能为 none，否则引擎无法确定扩展轴向。`,
      });
    }
  }

  // 3b-2. 比较运算符合法性（对齐 methodology/filter/filter.md 比较符全集：
  //       表达式符号 ==/!=/>=/<=/>/< 与过滤器单词形式 contains/notContains/
  //       startsWith/notStartsWith/endsWith/notEndsWith/in/notIn。
  //       非法 op 会在引擎端解析失败，提前拦截并给出完整候选集）。
  const OPS = new Set([
    '==', '!=', '>', '>=', '<', '<=',
    'contains', 'notContains', 'startsWith', 'notStartsWith',
    'endsWith', 'notEndsWith', 'in', 'notIn',
  ]);
  const checkOp = (loc: string, op?: string) => {
    if (op && !OPS.has(op)) {
      issues.push({
        level: 'error',
        cell: loc,
        message: `${loc}.op=${op}: 非法比较运算符（支持 ${[...OPS].join(' ')}）`,
      });
    }
  };
  for (const c of report.cells) {
    const where = coordName(c.row, c.col);
    if (c.content?.kind === 'dataset') {
      for (const cond of c.content.conditions ?? []) {
        checkOp(`${where}.content.conditions.${cond.left}`, cond.op);
      }
    }
    for (const r of c.style?.render ?? []) {
      checkOp(`${where}.style.render`, r.op);
    }
  }

  // 3c. 数据集字段类型 FieldType 仅支持 string/number/date/boolean，`datetime` 非法
  //     （引擎端无此类型、会被静默忽略或报错）。常见误写是把日期时间字段写成 datetime，
  //     正确应为 date。这里提前拦截并给出「是不是想写 date」建议。
  const FIELD_TYPES = new Set(['string', 'number', 'date', 'boolean']);
  const checkFieldType = (loc: string, t?: string) => {
    if (!t) return;
    if (t === 'datetime') {
      issues.push({
        level: 'error',
        cell: loc,
        message: `${loc}.type="datetime": 非法字段类型（FieldType 仅支持 string/number/date/boolean）。日期时间字段请改用 "date"。`,
      });
    } else if (!FIELD_TYPES.has(t)) {
      issues.push({
        level: 'error',
        cell: loc,
        message: `${loc}.type="${t}": 非法字段类型（FieldType 仅支持 string/number/date/boolean）。`,
      });
    }
  };
  for (const ds of report.datasets ?? []) {
    for (const f of ds.fields ?? []) checkFieldType(`datasets.${ds.name}.fields.${f.name}`, f.type);
  }

  // 3c1. 报表参数类型 ParameterType 与字段 FieldType 是两套独立体系：
  //      支持 string/number/boolean/date/datetime/list 六种（官方参数类型，
  //      `datetime` 在此处是独立合法类型，`list` 表示集合/多选）。数字用 `number`（无 Double）。
  const PARAM_TYPES = new Set(['string', 'number', 'boolean', 'date', 'datetime', 'list']);
  for (const p of report.parameters ?? []) {
    if (p.type && !PARAM_TYPES.has(p.type)) {
      issues.push({
        level: 'error',
        cell: `parameters.${p.name}`,
        message: `parameters.${p.name}.type="${p.type}": 非法参数类型（ParameterType 仅支持 string/number/boolean/date/datetime/list）。`,
      });
    }
  }

  // 3c2. BeanDataset（type=bean）仅允许 name/type/fields：datasource/sql/data/readFieldRemark 一律无效，
  //      序列化时被忽略、官方 XML 不认（官方格式 <dataset type="bean" name="..."> 仅含字段声明）。
  //      提前拦截并指向 dataset.md 的允许属性清单，避免部署后才发现。
  for (const ds of report.datasets ?? []) {
    if (ds.type !== 'bean') continue;
    for (const k of ['datasource', 'sql', 'data', 'readFieldRemark'] as const) {
      if ((ds as unknown as Record<string, unknown>)[k]) {
        issues.push({
          level: 'error',
          cell: `datasets.${ds.name}`,
          message: `数据集「${ds.name}」type=bean（BeanDataset）不允许属性 ${k}：BeanDataset 无数据源、无 SQL、无 readFieldRemark，仅声明 name/type/fields（见 dataset.md「BeanDataset 允许属性清单」）。`,
        });
      }
    }
  }

  // 3d. 交叉表角区必须使用 crosstab 内容类型（否则序列化为 text-content，不产生斜线表头）。
  //     识别：reportType === 'crosstab'，或同时存在向右/向下扩展的 dataset 单元格（交叉表指纹）。
  //     角区 kind:'crosstab' 还必须有 cornerLabels（row/col 分区文字），否则斜线表头无 <item> 文字。
  // 交叉表是显式概念（reportType:'crosstab'），不是"双向扩展"自动推断：
  // 主从明细表（表头横向 expand:right + 明细行纵向 expand:down）同样会同时出现两个方向，
  // 但它是默认报表类型，不应被强制要求交叉表角区（crosstab-corner）。模板导入的产物
  // 一律 source:'template' 且默认 reportType，天然不含交叉表语义。
  const isCrosstab = report.reportType === 'crosstab';
  // 弱指纹：仅作提示，不强制角区。帮助用户发现"可能想建交叉表却忘了声明"。
  const hasExpandRight = report.cells.some((c) => c.expand === 'right');
  const hasExpandDown = report.cells.some((c) => c.expand === 'down');
  if (hasExpandRight && hasExpandDown && !isCrosstab) {
    issues.push({
      level: 'warn',
      cell: 'A1',
      message:
        '检测到同时存在向右与向下扩展的单元格（主从明细布局常见）。若本报表是交叉表，请显式声明 reportType:"crosstab" 并给出 kind:"crosstab" 角区（含 cornerLabels）；若为普通主从明细表，可忽略本提示。',
    });
  }
  if (isCrosstab) {
    const cornerCells = report.cells.filter((c) => c.content?.kind === 'crosstab');
    if (cornerCells.length === 0) {
      issues.push({
        level: 'error',
        cell: 'grid',
        message:
          '交叉表（reportType:"crosstab"）必须有一个角区单元格使用 kind:"crosstab" 内容类型并给出 cornerLabels（列维度在前、行维度在后，如 {"col":["产品"],"row":["地区"]}），否则序列化为普通文本、不生成斜线表头。请在该角区单元格声明 kind:"crosstab"。',
      });
    } else {
      for (const c of cornerCells) {
        if (c.content?.kind !== 'crosstab') continue;
        const labels = c.content.cornerLabels;
        if (!labels || (labels.col ?? []).length === 0 || (labels.row ?? []).length === 0) {
          issues.push({
            level: 'error',
            cell: coordName(c.row, c.col),
            message: `${coordName(c.row, c.col)} 的 crosstab 角区必须提供 cornerLabels.row 与 cornerLabels.col 分区文字（列维度在前、行维度在后），否则斜线表头没有 <item> 文字。`,
          });
        }
      }
    }
  }

  // 3e. 父格（top/left）引用合法性——手工 IR 最易写错的隐式契约：
  //     ① 引用目标必须存在；② 不得指向角区（kind:'crosstab'，不参与行/列扩展）；
  //     ③ 交叉表内 top 必须指向列头（expand='right'）、left 必须指向行头（expand='down'）。
  //     典型错误如把取值格 top/left 写成 "A1"（角区），导致交叉表无法展开与聚合。
  const byCoord = new Map<string, CellIR>();
  for (const c of report.cells) byCoord.set(coordName(c.row, c.col), c);
  for (const c of report.cells) {
    const where = coordName(c.row, c.col);
    const refs = [
      { key: 'top', value: c.top },
      { key: 'left', value: c.left },
    ] as const;
    for (const { key, value } of refs) {
      if (!value || value === 'default' || value === 'none') continue;
      const target = byCoord.get(value);
      if (!target) {
        issues.push({
          level: 'error',
          cell: where,
          message: `${where}.${key}="${value}": 引用的父格不存在（坐标越界或未定义）。`,
        });
        continue;
      }
      if (target.content?.kind === 'crosstab') {
        issues.push({
          level: 'error',
          cell: where,
          message: `${where}.${key}="${value}" 指向交叉表角区：角区（斜线表头）不参与行/列扩展，不能作为父格。取值格 top 应指向列头（如 B1，expand='right'）、left 应指向行头（如 A2，expand='down'）。`,
        });
        continue;
      }
      if (isCrosstab) {
        if (key === 'top' && target.expand === 'down') {
          issues.push({
            level: 'error',
            cell: where,
            message: `${where}.top="${value}" 指向行方向扩展格（expand='down'）：交叉表中 top 应指向列头（expand='right'），如 B1。`,
          });
        }
        if (key === 'left' && target.expand === 'right') {
          issues.push({
            level: 'error',
            cell: where,
            message: `${where}.left="${value}" 指向列方向扩展格（expand='right'）：交叉表中 left 应指向行头（expand='down'），如 A2。`,
          });
        }
      }
    }
  }

  // 3f. 表达式规范预检——拦截 AI/手写 IR 最易犯的"Excel 思维"错误：
  //     ① 冒号范围引用（B2:D2，BaskReport 不支持，sum 等函数参数须逗号分隔）；
  //     ② 小写单元格引用（b2 会被当作变量名而非单元格坐标；$ 前缀的 $p1 是参数引用，不在此列）；
  //     ③ 缺 = 前缀（expression 必须为公式，纯文本请用 kind:'text'）；
  //     ④ 最外层被方括号包裹（[xxx] 非法；"[xxx]" 字符串字面量合法）；
  //     ⑤ 裸标识符（如 =sex?"男":"女" 的 sex：非函数调用、非大写单元格、非 $ 参数、
  //        非 var 局部变量、非关键字，引擎会当作未定义变量解析失败——字段值必须先
  //        绑定到单元格（content.kind:"dataset"），再以单元格引用判断，如 =if(B2=="男","男","女")）。
  //     仅对 content.kind === 'expression' 的字符串 value 做轻量启发式检查；
  //     合法语法（B2[B2:-1] 范围偏移、{B2} 页内引用、$C2 展开值、$p1 参数、
  //     user.sum(salary) 数据集聚合、函数调用等）均不触发。
  const EXPR_KEYWORDS = new Set([
    'var', 'return', 'if', 'else', 'true', 'false', 'null', 'and', 'or', 'in',
  ]);
  for (const c of report.cells) {
    const content = c.content;
    if (!content || content.kind !== 'expression') continue;
    if (typeof content.value !== 'string') continue;
    const where = coordName(c.row, c.col);
    const expr = content.value;
    const range = /[A-Z]+\d+\s*:\s*[A-Z]+\d+/.exec(expr);
    if (range) {
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.content.value 使用了 Excel 范围引用 "${range[0]}"：BaskReport 表达式不支持冒号范围，sum 等函数参数须逗号分隔（如 =sum(B2,C2,D2)）。`,
      });
    }
    // ② $ 前缀的是参数引用（如 $p1）或展开值（$C2），不属于小写单元格引用，用负向后行断言排除。
    const lower = /(?<!\$)\b[a-z]\d+\b/.exec(expr);
    if (lower) {
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.content.value 中小写单元格引用 "${lower[0]}"：单元格名称字母必须大写（${lower[0].toUpperCase()}，小写会被当作变量名解析）。`,
      });
    }
    // ④ 表达式最外层不能被方括号包裹（[xxx] 非法）。`[` 仅在展开取值/范围偏移时出现在内部，
    //    如 B2[B2:-1]、A1[]；若去掉可选 = 前缀后整个表达式以 `[` 开头，说明被 [] 包裹，非法。
    //    字符串字面量 "[xxx]" 以双引号开头，合法，不触发。
    if (expr.replace(/^=\s*/, '').startsWith('[')) {
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.content.value 最外层不能以方括号包裹（表达式首字符为 [；如需文本 "[xxx]" 应写成带双引号的字符串字面量）。`,
      });
    }
    if (!expr.startsWith('=')) {
      issues.push({
        level: 'warn',
        cell: where,
        message: `${where}.content.value 需以 = 前缀（如 =sum(B2)）；纯文本内容请改用 kind:"text"。`,
      });
    }
    // ⑤ 裸标识符（见上注释）：非函数调用、非大写单元格、非 $ 参数、非 var 变量、非关键字、
    //    非数据集聚合字段名的裸单词一律拦截——引擎按未定义变量解析失败。
    const varNames = new Set<string>();
    for (const m of expr.matchAll(/\bvar\s+([A-Za-z_][A-Za-z0-9_]*)/g)) varNames.add(m[1]!);
    // 受保护区间：字符串字面量、"数据集.聚合(字段名)" 整体（user.sum(salary) 的字段名合法）。
    const protectedRanges: Array<[number, number]> = [];
    for (const m of expr.matchAll(/"([^"\\]|\\.)*"/g)) {
      protectedRanges.push([m.index!, m.index! + m[0].length]);
    }
    for (const m of expr.matchAll(
      /[A-Za-z_][A-Za-z0-9_]*\s*\.\s*(?:count|countd|max|min|sum|avg)\s*\(\s*[A-Za-z_][A-Za-z0-9_]*\s*\)/g,
    )) {
      protectedRanges.push([m.index!, m.index! + m[0].length]);
    }
    const inProtected = (idx: number) => protectedRanges.some(([s, e]) => idx >= s && idx < e);
    // 裸标识符 = 字母/下划线开头；前一个字符不能是 \w $ . # @（排除 $p1/$C2/@billDate 等）。
    // 匹配出完整 token 后，再判断后随字符：紧跟 ( 或 . 的是函数调用 / 数据集名，不在此列。
    // （不能把 `(?!\s*[\(.])` 写进正则——它会与贪婪量词回溯出单字母，如 if 变成 i。）
    const bareRe = /(?<![\w$.#@])[A-Za-z_][A-Za-z0-9_]*/g;
    for (const m of expr.matchAll(bareRe)) {
      const name = m[0];
      const after = expr.slice(m.index! + name.length);
      const wsLen = after.match(/^\s*/)?.[0].length ?? 0;
      if (after[wsLen] === '(' || after[wsLen] === '.') continue; // 函数调用 / 数据集名
      if (EXPR_KEYWORDS.has(name)) continue;
      if (varNames.has(name)) continue;
      if (/^[A-Za-z]+\d+$/.test(name)) continue; // 单元格引用（B2 合法；小写 b2 由 ② 拦截）
      if (inProtected(m.index!)) continue;
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.content.value 出现未定义的裸标识符 "${name}"：表达式只能引用 baskserver 提供的函数（后跟 "("，如 if/formatDate/sum/dataset，见 expression.md 函数表）、大写单元格引用（如 B2）、参数（$p1）或数据集聚合（user.sum(salary)），不能直接写数据集字段名。字段值请先绑定到单元格（content.kind:"dataset"）再以单元格引用判断，如 =if(B2=="男","男","女")。`,
      });
      break; // 与 ①② 一致，只报首个违规
    }
  }

  // 3g. 数据映射（facade）结构校验——对齐官方内容渲染器规范：
  //     mappingType 仅允许 custom/dataset；custom 必须带非空 customMappingData 且每项
  //     label/value 非空；dataset 必须 datasetName/valueField/labelField 齐备且不得混带
  //     customMappingData。
  for (const c of report.cells) {
    const facade = c.facade;
    if (!facade) continue;
    const where = coordName(c.row, c.col);
    const mt = facade.mappingType ?? 'custom';
    if (mt !== 'custom' && mt !== 'dataset') {
      issues.push({
        level: 'error',
        cell: where,
        message: `${where}.facade.mappingType 取值 "${mt}" 非法：仅支持 "custom"（自定义键值对）或 "dataset"（数据集映射），对应 XML 的 mapping-type 属性。`,
      });
      continue;
    }
    if (mt === 'custom') {
      if (!facade.customMappingData?.length) {
        issues.push({
          level: 'error',
          cell: where,
          message: `${where}.facade：mappingType 为 "custom" 时必须提供非空 customMappingData（value→label 键值对），对应 XML 的 <custom-mapping-data label value/>。`,
        });
      } else {
        for (const [i, d] of facade.customMappingData.entries()) {
          if (d.label === undefined || d.label === '' || d.value === undefined || d.value === '') {
            issues.push({
              level: 'error',
              cell: where,
              message: `${where}.facade.customMappingData[${i}] 缺少 label 或 value：自定义映射每项必须同时提供 value 与 label。`,
            });
          }
        }
      }
    } else {
      if (!facade.datasetName || !facade.valueField || !facade.labelField) {
        issues.push({
          level: 'error',
          cell: where,
          message: `${where}.facade：mappingType 为 "dataset" 时必须齐备 datasetName/valueField/labelField（对应 XML 的 dataset/value-field/label-field 属性）。`,
        });
      }
      if (facade.customMappingData?.length) {
        issues.push({
          level: 'error',
          cell: where,
          message: `${where}.facade：mappingType 为 "dataset" 时不得混带 customMappingData（数据集映射不使用自定义键值对）。`,
        });
      }
    }
  }

  // 4. 参数占位符拼写校验（仅当报表声明了 parameters 或数据集 SQL 含占位符）
  const paramDefs = report.parameters ?? [];
  const paramNames = paramDefs.map((p) => p.name);
  const declaredParamSet = new Set(paramNames.map((n) => n.replace(/^\$/, '')));

  // 4a. 参数名必须合法（参数名本身不含 `$`，`$` 仅属于占位符语法）
  for (const p of paramDefs) {
    if (!isParamName(p.name)) {
      issues.push({
        level: 'error',
        message: `参数「${p.name}」命名不规范：参数名本身不应带前导 \`$\`（如 \`dept\`），SQL 中对应占位符才写为 \`\${$dept}\`。`,
      });
    }
  }

  // 4b. 收集所有数据集 SQL 中实际引用的占位符，与 parameters 比对
  const usedBare = new Set<string>();
  for (const ds of report.datasets ?? []) {
    if (ds.type !== 'sql' || !ds.sql) continue;
    for (const bare of extractParamPlaceholders(ds.sql)) {
      usedBare.add(bare);
      if (!declaredParamSet.has(bare)) {
        const suggest = suggestParamName(bare, paramNames);
        const hint = suggest ? ` 你是不是想写 \`\${$${suggest}}\`？` : '';
        issues.push({
          level: 'error',
          message: `数据集「${ds.name}」的 SQL 引用了未定义的参数占位符 \`\${$${bare}}\`：${hint}请在 parameters 中声明 \`{ "name": "${bare}" }\`。`,
        });
      }
    }
  }

  // 4b-1. 表达式上下文的参数引用扫描（groupExpression / 表达式单元格 / 条件样式 / condition 表达式值）。
  //       表达式里参数必须写裸 `$name`（如 if(pos>$p1)），禁止 `${$p1}` 包裹——表达式本身已按表达式解析，
  //       `${}` 仅用于字符串上下文（如数据集 SQL）告诉引擎括号内是表达式。
  //       同时 `$A1`（大写字母+数字）是单元格展开值引用，不是参数，需排除。
  //       ① 收集裸 `$name` 计入 usedBare → 参数仅用于表达式时消除 4c 误报；
  //       ② 表达式里出现 `${$name}` → warn，拦截 `${}` 误写；
  //       ③ 未声明的裸 `$name`（非 A1 坐标形态）→ warn + 拼写建议。
  const CELL_REF_RE = /^[A-Z]+\d+$/;
  const WRAPPED_PARAM_RE = /\$\{\$([A-Za-z_][A-Za-z0-9_]*)\}/g;
  const BARE_PARAM_RE = /\$([A-Za-z_][A-Za-z0-9_]*)/g;
  // 非法参数引用：`@param` 不是本引擎语法（参数在表达式中写裸 `$name`、字符串上下文写
  // `${$name}`，参数名本身不带任何前缀）。LLM 易凭空臆造 `@param`（如 Excel `@` 引用或其它
  // 报表工具语法），必须在结构自检阶段直接拦截，避免"赌渲染"通过但运行时静默失败。
  const AT_PARAM_RE = /@([A-Za-z_][A-Za-z0-9_]*)/g;
  const collectExprParamUsages = (loc: string, expr?: string) => {
    if (!expr || typeof expr !== 'string') return;
    for (const m of expr.matchAll(AT_PARAM_RE)) {
      const name = m[1]!;
      // 放行常见函数名/系统变量（如 @PageNumber 这类引擎保留字按需在此白名单扩展）
      if (name === 'PageNumber' || name === 'PageCount' || name === 'PageTotal') continue;
      issues.push({
        level: 'error',
        cell: loc,
        message: `${loc}（表达式上下文）出现了非法参数引用 \`@${name}\`：本引擎参数名不带 \`@\` 前缀。表达式中参数写裸 \`$${name}\`（如 \`=$${name}\`），字符串上下文（SQL/URL）写 \`\${$${name}}\`。请删除 \`@\` 前缀。`,
      });
    }
    for (const m of expr.matchAll(WRAPPED_PARAM_RE)) {
      const name = m[1]!.replace(/^\$/, '');
      issues.push({
        level: 'warn',
        cell: loc,
        message: `${loc}（表达式上下文）里参数被写成 \`${m[0]}\`：表达式本身已按表达式解析，参数请直接写裸 \`$${name}\`（如 if(pos>$${name})），不要再用 \${} 包裹；\${} 仅用于字符串上下文（如数据集 SQL）。`,
      });
    }
    for (const m of expr.matchAll(BARE_PARAM_RE)) {
      const name = m[1]!;
      if (CELL_REF_RE.test(name)) continue; // $A1 是单元格展开值引用
      usedBare.add(name);
      if (!declaredParamSet.has(name)) {
        const suggest = suggestParamName(name, paramNames);
        const hint = suggest ? ` 你是不是想写 \`$${suggest}\`？` : '';
        issues.push({
          level: 'warn',
          cell: loc,
          message: `${loc}（表达式上下文）引用了未声明的参数 \`$${name}\`：${hint}请在 parameters 中声明 \`{ "name": "${name}" }\`；若 \`$${name}\` 是单元格展开值引用（如 $A1），请忽略此提示。`,
        });
      }
    }
  };
  for (const c of report.cells) {
    const where = coordName(c.row, c.col);
    const content = c.content;
    if (content?.kind === 'expression') {
      collectExprParamUsages(`${where}.content.value`, content.value);
    } else if (content?.kind === 'dataset') {
      collectExprParamUsages(`${where}.content.groupExpression`, content.groupExpression);
      for (const cond of content.conditions ?? []) {
        if (cond.valueType === 'expression') {
          collectExprParamUsages(`${where}.content.conditions.${cond.left}.value`, cond.value);
        }
      }
    }
    for (const r of c.style?.render ?? []) {
      collectExprParamUsages(`${where}.style.render.leftExpression`, r.leftExpression);
      collectExprParamUsages(`${where}.style.render.rightExpression`, r.rightExpression);
    }
  }

  // 4c. parameters 中声明但 SQL/表达式都未使用的参数 → warn（可能是拼写不一致）
  for (const p of paramDefs) {
    const bare = p.name.replace(/^\$/, '');
    if (!usedBare.has(bare)) {
      issues.push({
        level: 'warn',
        message: `参数「${p.name}」已在 parameters 声明，但没有数据集 SQL 或表达式引用它（占位符 \`\${$${bare}}\` 未出现在任何 SQL，裸 \`$${bare}\` 也未出现在任何表达式），请确认是否拼写不一致或多余。`,
      });
    }
  }

  // 4d. editorType 合法性 + select 必须配 binding（覆盖 parameterForm 与 parameters[] 两条路径）。
  // 合法值含官方标准集及常用别名（serialize 会归一为 text/select/date/button）。
  const KNOWN_EDITOR_TYPES = new Set([
    'text', 'textarea', 'input',
    'select', 'dropdown', 'combo', 'combobox',
    'date', 'datepicker', 'calendar',
    'button',
  ]);
  const collectEditorUsages = () => {
    const usages: Array<{ src: string; editorType?: string; binding?: ParameterIR['binding'] }> = [];
    for (const fe of report.parameterForm ?? []) {
      usages.push({ src: `parameterForm.${fe.name}`, editorType: fe.editorType, binding: fe.binding });
    }
    for (const p of paramDefs) {
      usages.push({ src: `parameters.${p.name}`, editorType: p.editorType, binding: p.binding });
    }
    return usages;
  };
  for (const u of collectEditorUsages()) {
    const et = (u.editorType ?? 'text').toLowerCase();
    if (!KNOWN_EDITOR_TYPES.has(et)) {
      issues.push({
        level: 'error',
        message: `${u.src}.editorType="${u.editorType}"：非法编辑器类型（支持 text/select/date/button 及其别名 textarea/dropdown/datepicker 等）。`,
      });
      continue;
    }
    const norm = EDITOR_TYPE_ALIAS[et] ?? et;
    if (norm === 'select' && (!u.binding || (u.binding.customData ?? []).length === 0)) {
      issues.push({
        level: 'error',
        message: `${u.src}.editorType="select"：下拉控件必须配置 binding.customData 提供可选项。`,
      });
    }
  }

  // 5. 单元格坐标唯一性（name 与 row/col 一致性）
  const seen = new Map<string, string>();
  for (const g of grid.cells) {
    const name = g.cell.name!;
    const expected = coordName(g.cell.row, g.cell.col);
    if (g.cell.name && g.cell.name !== expected && g.cell.rowspan === 1 && g.cell.colspan === 1) {
      // 允许显式指定 name，但需与坐标一致才能被引擎识别
      issues.push({
        level: 'warn',
        cell: name,
        message: `单元格 name=${name} 与坐标 (${g.cell.row},${g.cell.col}) 推导的 ${expected} 不一致`,
      });
    }
    if (seen.has(name)) {
      issues.push({ level: 'error', cell: name, message: `坐标 ${name} 重复定义` });
    }
    seen.set(name, name);
  }

  // 5. 合并覆盖格不应携带内容（被覆盖格仍可能被给出内容，提示）
  // （expandGrid 已确保被覆盖格不输出；此处不再重复）

  // 6. 兜底拦截：典型「模板转换却漏搬行列」的最坏情况。
  //    特征：声明了 rowCount/columnCount（说明有版式意图）、单元格数量较多（>50，典型模板报表），
  //    却既无 --require-grid 也未声明 source:'template'，且确实缺 rows/columns。
  //    此时序列化会静默补默认行高 26/列宽 80 导致版式失真，故直接报错拦截，
  //    不依赖 AI 是否传 flag 或标 source。
  if (
    !mustRequireGrid &&
    (report.rowCount || report.columnCount) &&
    (report.cells?.length ?? 0) > 50 &&
    (!report.rows || report.rows.length === 0 || !report.columns || report.columns.length === 0)
  ) {
    if (!report.rows || report.rows.length === 0) {
      issues.push({
        level: 'error',
        message:
          '疑似模板转换报表但缺失 rows 行高定义：检测到 rowCount 且单元格数量较多，但 IR 无 rows[].height。模板报表必须从骨架原样搬入 rows/columns（或显式声明 "source": "template" 触发强制校验），否则全部行退化为默认高度 26，版式失真。',
      });
    }
    if (!report.columns || report.columns.length === 0) {
      issues.push({
        level: 'error',
        message:
          '疑似模板转换报表但缺失 columns 列宽定义：检测到 columnCount 且单元格数量较多，但 IR 无 columns[].width。模板报表必须从骨架原样搬入 rows/columns（或显式声明 "source": "template" 触发强制校验），否则全部列退化为默认宽度 80，版式失真。',
      });
    }
  }

  return issues;
}

export interface ExprItem {
  expr: string;
  where: string; // 可定位到具体单元格/字段，如 A2.content.value
}

/**
 * 收集报表中所有表达式，供在线 checkSyntaxBatch 权威校验（baskserver 设计器引擎裁决）：
 * - expression 单元格的 content.value（如 =sum(B2)）
 * - dataset 单元格的 content.groupExpression（如 formatDate(#,"yyyy")）
 * - dataset 单元格 conditions[] 中 valueType==='expression' 的 cond.value
 * - 条件样式 style.render[].leftExpression / rightExpression（如 C4>1000）
 * 返回顺序与 IR 单元格遍历顺序一致；调用方可视需要按 expr 去重。
 */
export function collectExpressions(report: ReportIR): ExprItem[] {
  const out: ExprItem[] = [];
  for (const c of report.cells) {
    const where = coordName(c.row, c.col);
    const content = c.content;
    if (content?.kind === 'expression') {
      if (typeof content.value === 'string' && content.value.trim()) {
        out.push({ expr: content.value, where: `${where}.content.value` });
      }
    } else if (content?.kind === 'dataset') {
      if (typeof content.groupExpression === 'string' && content.groupExpression.trim()) {
        out.push({ expr: content.groupExpression, where: `${where}.content.groupExpression` });
      }
      for (const cond of content.conditions ?? []) {
        if (cond.valueType === 'expression' && typeof cond.value === 'string' && cond.value.trim()) {
          out.push({ expr: cond.value, where: `${where}.content.conditions.${cond.left}.value` });
        }
      }
    }
    for (const r of c.style?.render ?? []) {
      if (r.leftExpression && r.leftExpression.trim()) {
        out.push({ expr: r.leftExpression, where: `${where}.style.render.leftExpression` });
      }
      if (r.rightExpression && r.rightExpression.trim()) {
        out.push({ expr: r.rightExpression, where: `${where}.style.render.rightExpression` });
      }
    }
  }
  return out;
}
